
import { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { usePainelSession } from '@/components/painel/rbac/painel-session-context';
import {
  buildAccountSettingsFromSession,
  validatePasswordChange,
  type AccountSettingsForm,
} from '@/lib/painel-account-settings';
import { setCachedAdminSession } from '@/lib/admin-auth';
import { changePassword, removeAvatar, uploadAvatar } from '@/services/auth.service';
import { resolveMediaUrl } from '@/services/api-client';
import {
  painelButtonOutlineClass,
  painelButtonPrimaryClass,
  painelDescriptionClass,
} from '@/components/painel/painel-styles';
import { cn } from '@/lib/utils';
import { AvatarUploader } from '@/components/painel/account-settings/AvatarUploader';
import { PersonalInfoCard } from '@/components/painel/account-settings/PersonalInfoCard';
import { SecurityCard } from '@/components/painel/account-settings/SecurityCard';
import { PreferencesCard } from '@/components/painel/account-settings/PreferencesCard';
import { SessionsCard } from '@/components/painel/account-settings/SessionsCard';
import { AppearanceCard } from '@/components/painel/account-settings/AppearanceCard';
import { AccountSettingsSkeleton } from '@/components/painel/account-settings/account-settings-shared';

export function PainelConfiguracoesPage() {
  const { session, role, hydrated, refresh } = usePainelSession();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [form, setForm] = useState<AccountSettingsForm | null>(null);
  const [initialForm, setInitialForm] = useState<AccountSettingsForm | null>(null);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarRemoved, setAvatarRemoved] = useState(false);

  useEffect(() => {
    if (!hydrated) return;
    if (!session || !role) {
      setIsLoading(false);
      return;
    }
    const timer = window.setTimeout(() => {
      const next = buildAccountSettingsFromSession(session);
      setForm(next);
      setInitialForm(next);
      setIsLoading(false);
    }, 420);
    return () => window.clearTimeout(timer);
  }, [hydrated, session, role]);

  const displayName = useMemo(() => {
    if (!form) return '';
    return [form.firstName, form.lastName].filter(Boolean).join(' ') || session?.name || '';
  }, [form, session?.name]);

  const patchForm = useCallback((patch: Partial<AccountSettingsForm>) => {
    setForm((prev) => (prev ? { ...prev, ...patch } : prev));
  }, []);

  const handleCancel = () => {
    if (initialForm) {
      if (form?.avatarPreview?.startsWith('blob:') && form.avatarPreview !== initialForm.avatarPreview) {
        URL.revokeObjectURL(form.avatarPreview);
      }
      setForm({ ...initialForm });
      setAvatarFile(null);
      setAvatarRemoved(false);
    }
    toast.message('Alterações descartadas', {
      description: 'O formulário foi restaurado ao estado anterior.',
    });
  };

  const handleAvatarChange = (preview: string | null, file?: File | null) => {
    if (preview?.startsWith('blob:')) {
      setAvatarFile(file ?? null);
      setAvatarRemoved(false);
    } else if (preview === null) {
      setAvatarFile(null);
      setAvatarRemoved(true);
    } else {
      setAvatarFile(null);
      setAvatarRemoved(false);
    }
    patchForm({ avatarPreview: preview });
  };

  const handleSave = async () => {
    if (!form || !initialForm) return;

    const wantsPasswordChange = Boolean(form.newPassword || form.confirmPassword);
    const wantsAvatarUpload = avatarFile !== null;
    const wantsAvatarRemove =
      avatarRemoved && Boolean(initialForm.avatarPreview) && !wantsAvatarUpload;

    if (!wantsPasswordChange && !wantsAvatarUpload && !wantsAvatarRemove) {
      toast.message('Nada para salvar', {
        description: 'Altere o avatar ou a senha antes de guardar.',
      });
      return;
    }

    const passwordError = validatePasswordChange({
      newPassword: form.newPassword,
      confirmPassword: form.confirmPassword,
    });
    if (passwordError) {
      toast.error('Verifique a senha', { description: passwordError });
      return;
    }
    if (wantsPasswordChange && !form.currentPassword) {
      toast.error('Verifique a senha', {
        description: 'Informe a sua senha atual para confirmar a alteração.',
      });
      return;
    }

    setIsSaving(true);
    let nextAvatarPreview = form.avatarPreview;

    try {
      if (wantsPasswordChange) {
        await changePassword(form.currentPassword, form.newPassword);
      }

      if (wantsAvatarUpload && avatarFile) {
        const updated = await uploadAvatar(avatarFile);
        setCachedAdminSession(updated);
        nextAvatarPreview = resolveMediaUrl(updated.avatarUrl);
      } else if (wantsAvatarRemove) {
        const updated = await removeAvatar();
        setCachedAdminSession(updated);
        nextAvatarPreview = null;
      }

      refresh();
    } catch (err) {
      toast.error('Não foi possível guardar', {
        description: err instanceof Error ? err.message : 'Tente novamente.',
      });
      setIsSaving(false);
      return;
    }
    setIsSaving(false);

    if (form.avatarPreview?.startsWith('blob:')) {
      URL.revokeObjectURL(form.avatarPreview);
    }

    const savedSnapshot = {
      ...form,
      avatarPreview: nextAvatarPreview,
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    };
    setForm(savedSnapshot);
    setInitialForm(savedSnapshot);
    setAvatarFile(null);
    setAvatarRemoved(false);

    const parts: string[] = [];
    if (wantsAvatarUpload || wantsAvatarRemove) parts.push('avatar');
    if (wantsPasswordChange) parts.push('senha');

    toast.success('Alterações guardadas', {
      description:
        parts.length === 2
          ? 'O seu avatar e a nova senha já estão activos.'
          : parts[0] === 'avatar'
            ? 'O seu avatar já aparece no painel e nos guias.'
            : 'A sua nova senha já está activa.',
      duration: 5000,
    });
  };

  if (!hydrated || isLoading || !form || !session || !role) {
    return <AccountSettingsSkeleton />;
  }

  return (
    <div className="space-y-10 pb-8">
      <motion.header
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className="space-y-2"
      >
        <p className="font-inter text-[10px] font-bold uppercase tracking-[0.2em] text-sky-300/55">
          Conta
        </p>
        <h1 className="font-poppins text-2xl font-bold tracking-tight text-white md:text-3xl">
          Configurações da Conta
        </h1>
        <p className={cn('max-w-2xl text-sm leading-relaxed', painelDescriptionClass)}>
          Gerencie suas informações pessoais, segurança e preferências da sua conta administrativa.
        </p>
      </motion.header>

      <div className="space-y-6">
        <AvatarUploader
          displayName={displayName}
          role={role}
          email={form.email}
          avatarPreview={form.avatarPreview}
          onAvatarChange={handleAvatarChange}
          delay={0.05}
        />

        <PersonalInfoCard
          firstName={form.firstName}
          lastName={form.lastName}
          username={form.username}
          email={form.email}
          bio={form.bio}
          onChange={patchForm}
          delay={0.1}
        />

        <SecurityCard
          currentPassword={form.currentPassword}
          newPassword={form.newPassword}
          confirmPassword={form.confirmPassword}
          onChange={patchForm}
          delay={0.15}
        />

        <PreferencesCard
          preferences={form.preferences}
          onChange={(preferences) => patchForm({ preferences })}
          delay={0.2}
        />

        <SessionsCard delay={0.25} />

        <AppearanceCard delay={0.3} />
      </div>

      <motion.footer
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
        className={cn(
          'sticky bottom-4 z-20 flex flex-col-reverse gap-3 rounded-2xl border border-white/10',
          'bg-[#030023]/90 p-4 shadow-[0_8px_40px_-12px_rgba(0,0,0,0.65)] backdrop-blur-xl sm:flex-row sm:items-center sm:justify-end'
        )}
      >
        <Button
          type="button"
          variant="ghost"
          className={cn(painelButtonOutlineClass, 'h-11 px-6 sm:mr-auto')}
          onClick={handleCancel}
          disabled={isSaving}
        >
          Cancelar
        </Button>
        <Button
          type="button"
          className={cn(painelButtonPrimaryClass, 'h-11 min-w-[160px] px-6')}
          onClick={handleSave}
          disabled={isSaving}
        >
          {isSaving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Salvando…
            </>
          ) : (
            'Salvar alterações'
          )}
        </Button>
      </motion.footer>
    </div>
  );
}
